import React, { useState, useRef, useEffect } from 'react';
import { STATIC_BASE_URL } from '../config';

// Campus Map Component
function CampusMap({ coordinates, buildings, onBuildingClick }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const containerRef = useRef(null);
  const [, setViewportVersion] = useState(0);
  
  // Debug için koordinatları logla
  console.log('CampusMap - Received coordinates:', coordinates);
  
  // 14 farklı renk paleti
  const colorPalette = [
    '#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22',
    '#34495e', '#f1c40f', '#e91e63', '#00bcd4', '#4caf50', '#ff9800', '#795548'
  ];

  const handlePointClick = (coord) => {
    // Find corresponding building
    const building = buildings.find(b => b.entrance_name === coord.building_name);
    if (building) {
      onBuildingClick(building.building_name);
    }
  };

      const handleImageLoad = () => {
      console.log('Map image loaded successfully');
      setImageLoaded(true);
      setImageError(false);
      setShowPlaceholder(false);
    };

  const handleImageError = () => {
    console.error('Map image failed to load');
    setImageError(true);
    setImageLoaded(false);
    setShowPlaceholder(false);
  };

  const BASE_WIDTH = 800;
  const BASE_HEIGHT = 450;

  const toClientPosition = (x, y) => {
    const container = containerRef.current;
    if (!container) return { left: 0, top: 0 };
    const rect = container.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    // contain: görüntünün tamamı görünür
    const scale = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
    const displayedWidth = BASE_WIDTH * scale;
    const displayedHeight = BASE_HEIGHT * scale;
    const offsetX = (width - displayedWidth) / 2;
    const offsetY = (height - displayedHeight) / 2;
    return { left: offsetX + x * scale, top: offsetY + y * scale };
  };

  useEffect(() => {
    const handleResize = () => {
      setViewportVersion(v => v + 1);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="campus-map">
      <div className="map-container" ref={containerRef}>

        
        {/* Loading State */}
        {!imageLoaded && !imageError && (
          <div className="map-loading">
            <div className="map-skeleton">
              <div className="skeleton-placeholder"></div>
              <div className="loading-text">Harita yükleniyor...</div>
            </div>
          </div>
        )}
        
        {/* Error State */}
        {imageError && (
          <div className="map-error">
            <p>Harita yüklenirken hata oluştu</p>
            <button onClick={() => window.location.reload()}>Tekrar Dene</button>
          </div>
        )}
        
        {/* Placeholder Image */}
        {showPlaceholder && (
          <img 
            src={`${STATIC_BASE_URL}/static/images/campus_map_placeholder.jpg`} 
            alt="Kampüs Haritası (Yükleniyor)"
            className="map-image placeholder"
            style={{ 
              filter: 'blur(2px)',
              width: '100%',
              height: '600px',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
              margin: 0,
              padding: 0
            }}
            onError={(e) => {
              console.log('Placeholder image failed to load');
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        
        {/* Main Image */}
        <img 
          src={`${STATIC_BASE_URL}/static/images/campus_map_small.jpg`} 
          alt="Kampüs Haritası"
          className={`map-image ${imageLoaded ? 'loaded' : ''}`}
          style={{
            width: '100%',
            height: '600px',
            objectFit: 'contain',
            objectPosition: 'center',
            display: 'block',
            margin: 0,
            padding: 0
          }}
          onLoad={handleImageLoad}
          onError={(e) => {
            console.error('Main map image failed to load:', e.currentTarget.src);
            // Try fallback image
            if (e.currentTarget.src.includes('campus_map_small.jpg')) {
              e.currentTarget.src = `${STATIC_BASE_URL}/static/images/campus_map_medium.jpg`;
            } else if (e.currentTarget.src.includes('campus_map_medium.jpg')) {
              e.currentTarget.src = `${STATIC_BASE_URL}/static/images/campus_map.jpg`;
            } else {
              handleImageError();
            }
          }}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        
        {/* Render clickable points */}
        {coordinates.map((coord, index) => {
          const { left, top } = toClientPosition(coord.x, coord.y);
          return (
            <div
              key={index}
              className="map-point"
              style={{
                left: `${left}px`,
                top: `${top}px`,
              }}
              onClick={() => handlePointClick(coord)}
              title={coord.building_name}
            >
              <div 
                className="point-marker"
                style={{
                  width: '48px',
                  height: '48px',
                  cursor: 'pointer',
                  position: 'relative'
                }}
              >
                <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path 
                    fill={colorPalette[index % colorPalette.length]} 
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                    stroke="#fff"
                    strokeWidth="1"
                  />
                  <circle cx="12" cy="9" r="2" fill="white"/>
                </svg>
              </div>
            </div>
          );
        })}
      </div>

      <div className="map-legend">
        <h3>📍 Giriş Kapıları</h3>
        <div className="legend-items">
          {coordinates.map((coord, index) => (
            <div 
              key={index}
              className="legend-item"
              onClick={() => handlePointClick(coord)}
            >
              <div className="legend-marker">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path 
                    fill={colorPalette[index % colorPalette.length]} 
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                    stroke="#fff"
                    strokeWidth="1"
                  />
                  <circle cx="12" cy="9" r="2" fill="white"/>
                </svg>
              </div>
              <span className="legend-text">
                {coord.building_name === 'FANTOM VE PRAKİLİNİK LABORATUVARI GİRİŞ KAPISI' 
                  ? 'Fantom ve Prakilinik Laboratuvarı ve Konferans Salonu' 
                  : coord.building_name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CampusMap;
